import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationType } from '../../enums/NotificationType';
import { formatMultiplier, OptionType } from '../../enums/OptionType';
import { OnlineOrder, OnlineOrderStatus, OnlineOrderType } from '../../models/OnlineOrder.model';
import { AppSettingsService } from '../../services/app-settings.service';
import { CartService, parseUtcEpochMs } from '../../services/cart.service';
import { WaiterCallService } from '../../services/waiter-call.service';
import { FormatPricePipe } from '../../utility/pipes/format-price.pipe';
import { TextUtils } from '../../utility/text.utils';

@Component({
  selector: 'app-order-status-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormatPricePipe],
  templateUrl: './order-status-modal.html',
})
export class OrderStatusModalComponent implements OnInit, OnDestroy {
  public cartService = inject(CartService);
  public settingsService = inject(AppSettingsService);
  public waiterService = inject(WaiterCallService);

  NotificationType = NotificationType;
  OnlineOrderStatus = OnlineOrderStatus;
  OnlineOrderType = OnlineOrderType;
  OptionType = OptionType;
  formatMultiplier = formatMultiplier;

  activeMenuLang = this.settingsService.activeMenuLanguage;
  currency = this.settingsService.selectedCurrency;

  private timerInterval: any = null;
  public currentTime = Date.now();

  get companyCurrency(): string {
    return this.currency()?.Symbol || '$';
  }

  get currencyPrecision(): number {
    const p = this.currency()?.Precision;
    return typeof p === 'number' && !isNaN(p) ? p : 2;
  }

  ngOnInit() {
    this.timerInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 10000);
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  activeOrders = this.cartService.activeOrders;
  order = this.cartService.activeOrder;
  selectedOrderId = this.cartService.selectedTrackingOrderId;
  totalAllActiveOrders = this.cartService.totalAllActiveOrders;

  status = computed(() => this.order()?.Status ?? OnlineOrderStatus.PENDING);

  statusKey = computed(() => {
    switch (this.status()) {
      case OnlineOrderStatus.PENDING:
        return 'PENDING';
      case OnlineOrderStatus.ACCEPTED:
        return 'ACCEPTED';
      case OnlineOrderStatus.PREPARING:
        return 'PREPARING';
      case OnlineOrderStatus.READY:
        return 'READY';
      case OnlineOrderStatus.OUT_FOR_DELIVERY:
        return 'OUT_FOR_DELIVERY';
      case OnlineOrderStatus.DELIVERED:
        return 'DELIVERED';
      case OnlineOrderStatus.REJECTED:
        return 'REJECTED';
      case OnlineOrderStatus.CANCELLED:
        return 'CANCELLED';
      default:
        return 'UNKNOWN';
    }
  });

  formattedReadyTime = computed(() => {
    const prepTime = this.order()?.PrepTime;
    if (!prepTime || prepTime <= 0) return null;
    const date = new Date(prepTime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  });

  remainingMinutes = computed(() => {
    const prepTime = this.order()?.PrepTime;
    if (!prepTime || prepTime <= 0) return null;
    const diffMs = prepTime - this.currentTime;
    const mins = Math.ceil(diffMs / 60000);
    return mins > 0 ? mins : 0;
  });

  getDisplayText(value: any): string {
    if (typeof value === 'string' && value.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(value);
        return TextUtils.getLocalizedText(parsed, this.activeMenuLang()?.Code);
      } catch (_) { }
    }
    return TextUtils.getLocalizedText(value, this.activeMenuLang()?.Code);
  }

  getItemFeatures(features: any): any[] {
    if (!features) return [];
    if (Array.isArray(features)) return features;
    if (typeof features === 'string') {
      try {
        const parsed = JSON.parse(features);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) { }
    }
    return [];
  }

  getOrderTabLabel(ord: OnlineOrder, index: number): string {
    let timeStr = '';
    if (ord && ord.created) {
      try {
        const d = new Date(parseUtcEpochMs(ord.created));
        if (!isNaN(d.getTime())) {
          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');
          timeStr = `${hours}:${minutes} `;
        }
      } catch (_) { }
    }
    return `${timeStr}#${index + 1}`;
  }

  selectOrder(id: string) {
    this.cartService.selectedTrackingOrderId.set(id);
  }

  dismissOrder(id: string) {
    this.cartService.dismissOrder(id);
  }

  getStatusDotClass(status?: OnlineOrderStatus): string {
    switch (status) {
      case OnlineOrderStatus.PENDING:
        return 'bg-amber-500 animate-pulse';
      case OnlineOrderStatus.ACCEPTED:
      case OnlineOrderStatus.PREPARING:
        return 'bg-emerald-500 animate-pulse';
      case OnlineOrderStatus.READY:
        return 'bg-green-600';
      case OnlineOrderStatus.REJECTED:
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  }

  close() {
    this.cartService.closeTracking();
  }

  dismiss() {
    const currentId = this.order()?.id;
    if (currentId) {
      this.cartService.dismissOrder(currentId);
    } else {
      this.cartService.dismissActiveOrder();
    }
  }
}
